import { useMemo, useState } from 'react';
import { AlertOctagon, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { IconBox, Modal, Stat } from '@/ui/primitives';
import { GridAuto } from '@/ui/layout';
import { nivelTone } from '../data';
import type { DiagnosticoList, DiagnosticoListItem, Nivel } from '../schemas';

type Tipo = Nivel;

const TITLES: Record<Tipo, string> = {
  critico: 'Clientes Críticos',
  atencao: 'Clientes em Atenção',
  regular: 'Clientes Regulares',
};

interface Props {
  data: DiagnosticoList;
  onSelectCpf: (cpf: string) => void;
}

export function DiagnosticoStats({ data, onSelectCpf }: Props) {
  const [tipoOpen, setTipoOpen] = useState<Tipo | null>(null);

  const counts = { critico: 0, atencao: 0, regular: 0 };
  for (const c of data.clientes) counts[c.nivel]++;

  return (
    <>
      <GridAuto min="sm" gap="md">
        <Stat
          label="Críticos"
          value={counts.critico}
          tone="danger"
          onClick={() => setTipoOpen('critico')}
          className="cursor-pointer hover:border-border2"
          icon={
            <IconBox tone="danger">
              <AlertOctagon size={16} strokeWidth={1.5} />
            </IconBox>
          }
        />
        <Stat
          label="Atenção"
          value={counts.atencao}
          tone="warn"
          onClick={() => setTipoOpen('atencao')}
          className="cursor-pointer hover:border-border2"
          icon={
            <IconBox tone="warn">
              <AlertTriangle size={16} strokeWidth={1.5} />
            </IconBox>
          }
        />
        <Stat
          label="Regulares"
          value={counts.regular}
          tone="ok"
          onClick={() => setTipoOpen('regular')}
          className="cursor-pointer hover:border-border2"
          icon={
            <IconBox tone="ok">
              <CheckCircle2 size={16} strokeWidth={1.5} />
            </IconBox>
          }
        />
      </GridAuto>

      <DiagnosticoStatModal
        tipo={tipoOpen}
        clientes={data.clientes}
        onClose={() => setTipoOpen(null)}
        onSelect={(cpf) => {
          // Fecha o modal primeiro; espera Radix limpar pointer-events do body
          // antes de abrir o DiagnosticoDetailModal (evita modal travado).
          setTipoOpen(null);
          setTimeout(() => onSelectCpf(cpf), 150);
        }}
      />
    </>
  );
}

interface ModalProps {
  tipo: Tipo | null;
  clientes: DiagnosticoListItem[];
  onClose: () => void;
  onSelect: (cpf: string) => void;
}

function DiagnosticoStatModal({ tipo, clientes, onClose, onSelect }: ModalProps) {
  const lista = useMemo(() => {
    if (!tipo) return [];
    return clientes
      .filter((c) => c.nivel === tipo)
      .sort((a, b) => a.score - b.score);
  }, [tipo, clientes]);

  return (
    <Modal
      open={tipo !== null}
      onOpenChange={(v) => !v && onClose()}
      title={tipo ? TITLES[tipo] : ''}
      size="md"
    >
      {lista.length === 0 ? (
        <p className="text-sm text-text-muted">Nenhum cliente nesta categoria.</p>
      ) : (
        <ul className="flex flex-col gap-1 max-h-[60vh] overflow-y-auto -mx-2">
          {lista.map((c) => (
            <li key={c.cpf}>
              <button
                type="button"
                onClick={() => onSelect(c.cpf)}
                className="w-full flex items-center gap-3 px-2 py-2 rounded-md text-left hover:bg-[var(--selection-soft)] transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-text truncate">{c.nome}</div>
                  {c.municipio && (
                    <div className="text-xs text-text-muted truncate">{c.municipio}</div>
                  )}
                </div>
                <ScoreBadge score={c.score} nivel={c.nivel} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function ScoreBadge({ score, nivel }: { score: number; nivel: Nivel }) {
  const tone = nivelTone(nivel);
  const color =
    tone === 'ok' ? 'var(--ok)' : tone === 'warn' ? 'var(--warning)' : 'var(--danger)';
  return (
    <span
      className="text-xs font-semibold tabular-nums px-2 py-0.5 rounded-md flex-shrink-0"
      style={{ color, backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)` }}
    >
      {Math.round(score)}%
    </span>
  );
}
